import { useCallback, useEffect, useState } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { supabase } from "./supabase";
import { db } from "./db";
import { ensureSession } from "./authState";

export const prettyPhone = (e164) => { try { return parsePhoneNumberFromString(e164 || "")?.formatInternational() || e164 || ""; } catch { return e164 || ""; } };

export const sortCrew = (rows) => [...rows].sort((a, b) => (b.use_count || 0) - (a.use_count || 0) || (b.last_used_at || "").localeCompare(a.last_used_at || "") || a.name.localeCompare(b.name));

export const findCrew = (rows, { phone, email }) => rows.find((c) => (phone && c.phone === phone) || (email && c.email && c.email.toLowerCase() === email.toLowerCase())) || null;

export const loadCrew = async () => {
  const cached = sortCrew(await db.all("crew"));
  const auth = await ensureSession();
  if (!auth.session) return { rows: cached, fromCache: true };
  const { data, error } = await supabase.from("crew_contacts").select("*");
  if (error) return { rows: cached, fromCache: true, error: /crew_contacts/.test(error.message) ? null : error.message };
  await db.clear("crew"); await db.putAll("crew", data);
  return { rows: sortCrew(data), fromCache: false };
};

// Upsert by phone/email. Returns the saved row (remote id) or a local pending row when offline.
export const upsertCrew = async (userId, { name, phone, email, role }, { allowLocal = true } = {}) => {
  const clean = { name: name.trim(), phone: phone || null, email: email ? email.trim().toLowerCase() : null, role: role?.trim() || null };
  const local = sortCrew(await db.all("crew"));
  const match = findCrew(local, clean);
  const auth = await ensureSession();
  if (!auth.session) {
    if (!allowLocal) throw new Error("offline");
    const row = { id: match?.id || `local:${crypto.randomUUID()}`, user_id: userId, use_count: 0, ...match, ...clean, pending: true };
    await db.put("crew", row);
    return row;
  }
  let q;
  if (match && !String(match.id).startsWith("local:")) q = supabase.from("crew_contacts").update(clean).eq("id", match.id).select().single();
  else {
    const { data: remote } = await supabase.from("crew_contacts").select("*").or([clean.phone && `phone.eq.${clean.phone}`, clean.email && `email.eq.${clean.email}`].filter(Boolean).join(","));
    const hit = remote?.[0];
    q = hit ? supabase.from("crew_contacts").update(clean).eq("id", hit.id).select().single() : supabase.from("crew_contacts").insert({ user_id: userId, ...clean }).select().single();
  }
  const { data, error } = await q;
  if (error) throw error;
  if (match && String(match.id).startsWith("local:")) await db.del("crew", match.id);
  await db.put("crew", data);
  return data;
};

export const deleteCrew = async (id) => {
  if (!String(id).startsWith("local:")) { const { error } = await supabase.from("crew_contacts").delete().eq("id", id); if (error) throw error; }
  await db.del("crew", id);
};

export const useCrew = () => {
  const [state, setState] = useState({ rows: null, fromCache: false, error: null });
  const refresh = useCallback(() => loadCrew().then((s) => setState({ error: null, ...s })), []);
  useEffect(() => { refresh(); window.addEventListener("online", refresh); return () => window.removeEventListener("online", refresh); }, [refresh]);
  return { ...state, refresh };
};
